import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Github, ExternalLink } from "lucide-react";
import { FadeUp } from "../Reveal";
import { projects } from "../../data/content";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Badge } from "../ui/badge";

const SPAN_CLASS = {
  hero: "sm:col-span-12 lg:col-span-7 lg:row-span-2 min-h-[300px] sm:min-h-[480px]",
  wide: "sm:col-span-12 lg:col-span-5 min-h-[260px]",
  tall: "sm:col-span-12 lg:col-span-5 min-h-[260px]",
  sm:   "sm:col-span-6 lg:col-span-6 min-h-[230px]"
};

const Tilt = ({ children, color }) => {
  const [t, setT] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setT({ x: -py * 5, y: px * 5 });
  };
  const onLeave = () => setT({ x: 0, y: 0 });
  return (
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ transformStyle: "preserve-3d" }}
      animate={{ rotateX: t.x, rotateY: t.y }}
      transition={{ type: "spring", stiffness: 200, damping: 18, mass: 0.4 }}
      className="h-full w-full relative"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 hover:opacity-100 transition-opacity"
        style={{ boxShadow: `0 0 0 1px ${color}40, 0 30px 80px -10px ${color}25` }}
      />
      {children}
    </motion.div>
  );
};

const ProjectCard = ({ project, index, onOpen }) => {
  return (
    <FadeUp delay={index * 0.04} className={`${SPAN_CLASS[project.span] || SPAN_CLASS.sm}`}>
      <Tilt color={project.color}>
        <button
          onClick={() => onOpen(project)}
          data-testid={`projects-card-${index}`}
          data-magnetic="true"
          className="group relative isolate h-full w-full overflow-hidden rounded-2xl border border-edge-650 bg-graphite-900/60 p-6 pb-16 text-left transition-colors hover:border-magenta-500/40"
        >
          {project.preview && (
            <img src={project.preview} alt="" aria-hidden loading="lazy"
                 className="absolute inset-0 -z-20 h-full w-full object-cover scale-110 blur-[5px] opacity-90 saturate-150 transition duration-500 group-hover:blur-[1.5px] group-hover:opacity-100" />
          )}
          <div className="absolute inset-0 -z-10 bg-gradient-to-t from-ink-950/95 via-ink-950/60 to-ink-950/10" />
          <div className="absolute inset-0 -z-10 opacity-60"
               style={{ background: `radial-gradient(60% 80% at 20% 0%, ${project.color}1a 0%, transparent 60%)` }} />
          <div className="absolute inset-0 -z-10 bg-grid opacity-40" />

          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500">{project.tag}</span>
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500">{project.year}</span>
          </div>

          <div className="mt-8 sm:mt-10">
            <h3 className="font-display text-2xl sm:text-3xl text-white tracking-tight">{project.name}</h3>
            <p className="mt-3 text-sm text-zinc-400 max-w-md">{project.blurb}</p>
          </div>

          <div className="mt-6 flex flex-wrap gap-1.5">
            {project.stack.slice(0, 4).map((s) => (
              <span key={s} className="inline-flex items-center rounded-full border border-edge-700 bg-ink-900 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300">{s}</span>
            ))}
            {project.stack.length > 4 && (
              <span className="inline-flex items-center rounded-full border border-edge-700 bg-ink-900 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300">+{project.stack.length - 4}</span>
            )}
          </div>

          <div className="absolute bottom-5 right-5">
            <span className="inline-flex items-center gap-1 rounded-xl border border-edge-700 px-2.5 py-1.5 text-xs text-zinc-200 group-hover:border-magenta-500/60 group-hover:text-white transition-colors">
              Case study <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </button>
      </Tilt>
    </FadeUp>
  );
};

export const Projects = () => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const onOpen = (p) => { setActive(p); setOpen(true); };

  return (
    <section id="projects" className="relative py-20 sm:py-28 border-t border-edge-650">
      <div className="container-page">
        <FadeUp>
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-magenta-300">Selected Work</div>
        </FadeUp>
        <FadeUp delay={0.05}>
          <h2 className="mt-4 font-display tracking-tight text-3xl sm:text-5xl text-white max-w-3xl leading-[1.05]">
            Things I've built, and a few I'm still building.
          </h2>
        </FadeUp>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 gap-4 auto-rows-[minmax(220px,auto)]">
          {projects.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} onOpen={onOpen} />
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-ink-900 border-edge-700 text-zinc-100 max-h-[90vh]">
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-4">
                  <Badge variant="secondary" className="bg-graphite-900 border border-edge-700 text-zinc-300 font-mono text-[10px] tracking-[0.18em] uppercase">{active.tag}</Badge>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">{active.year}</span>
                </div>
                <DialogTitle className="font-display text-2xl sm:text-3xl text-white tracking-tight">{active.name}</DialogTitle>
                <DialogDescription className="text-zinc-400 text-sm">{active.blurb}</DialogDescription>
              </DialogHeader>

              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-magenta-300 mb-2">Problem</div>
                  <p className="text-zinc-300 leading-relaxed">{active.problem}</p>
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-magenta-300 mb-2">Approach</div>
                  <p className="text-zinc-300 leading-relaxed">{active.approach}</p>
                </div>
                <div className="sm:col-span-2">
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-magenta-300 mb-2">Impact</div>
                  <ul className="space-y-2">
                    {active.impact.map((i) => (
                      <li key={i} className="flex gap-3 text-zinc-200"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-magenta-500 shrink-0" />{i}</li>
                    ))}
                  </ul>
                </div>
                <div className="sm:col-span-2">
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-magenta-300 mb-2">Stack</div>
                  <div className="flex flex-wrap gap-1.5">
                    {active.stack.map((s) => (
                      <span key={s} className="rounded-full border border-edge-700 bg-graphite-900 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300">{s}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 pt-4 border-t border-edge-700">
                {active.id === "immbrain" ? (
                  <Link to="/immbrain" onClick={() => setOpen(false)} className="inline-flex items-center gap-2 rounded-xl bg-red-600 text-white px-4 py-2 text-sm font-semibold hover:bg-red-500 transition-colors shadow-lg shadow-red-900/20">
                    View Live Tracker <ArrowUpRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <a href="#contact" onClick={() => setOpen(false)} className="inline-flex items-center gap-2 rounded-xl bg-magenta-500 text-ink-950 px-4 py-2 text-sm font-semibold hover:bg-magenta-300">
                    Discuss this project <ArrowUpRight className="w-4 h-4" />
                  </a>
                )}
                <span className="inline-flex items-center gap-2 rounded-xl border border-edge-700 px-3 py-2 text-xs text-zinc-400"><Github className="w-4 h-4" />Private repo</span>
                <span className="inline-flex items-center gap-2 rounded-xl border border-edge-700 px-3 py-2 text-xs text-zinc-400"><ExternalLink className="w-4 h-4" />Case study on request</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
